import { useMemo } from 'react';
import type {
  Company,
  Customer,
  Invoice,
  InvoiceItem,
  PrintFormat,
} from '@kss/api';
import { resolvePrintTheme } from '@kss/ui';
import { resolveTranslated } from '@kss/api';

/**
 * Built-in document title when the tenant hasn't set one.
 *
 * The colour fallbacks that used to live here now come from
 * `PRINT_THEME_DEFAULTS` in the shared resolver, so invoices and reports can
 * never drift to different defaults.
 */
const FALLBACK_TITLE = 'TAX INVOICE';

/**
 * Presentational, side-effect-free invoice document.
 *
 * Renders the exact print-ready layout shown in the reference PDF
 * `Invoice-KRPL-2026_02_001.pdf`. Extracted from `InvoicePreview.tsx`
 * so both the panel's admin preview route AND the customer's
 * `/account/invoices/:id` detail route share one visual source of
 * truth. Bug ref: SSO ISSUE 4 — "customer-facing invoices section
 * displays invoices incorrectly".
 *
 * Data-only — no fetches, no navigation, no toast. The calling page
 * resolves the signature image URL (each app has its own
 * `toAbsoluteImageUrl`) and passes it as `signatureSrc`.
 *
 * Layout (top to bottom):
 *   1. "TAX INVOICE" heading
 *   2. Invoice no / Date / Due Date (left) · Company logo (right)
 *   3. Billed By · Billed To
 *   4. Place of Supply · Country of Supply
 *   5. Item table
 *   6. Bank Details + Scan-and-pay QR · Invoice Summary
 *   7. Terms & Conditions · Amount In Words + Early-Pay discount
 *   8. Additional Notes · Authorized Signature
 *   9. Footer line
 */
export interface InvoiceDocumentProps {
  invoice: Invoice;
  items: InvoiceItem[];
  seller: Company | null;
  buyer: Customer | null;
  /**
   * Absolute (or same-origin) URL of the authorized-signature image.
   * When empty, the layout falls back to the dashed placeholder so
   * the block never collapses.
   */
  signatureSrc?: string;
  /**
   * Early-payment discount rate (%) offered on the printed document.
   *
   * Previously hardcoded to 2% and rendered on EVERY invoice — a binding
   * financial term no tenant had configured, which a customer could
   * reasonably claim. Now opt-in: `0`/omitted hides the block entirely.
   * Supplied by the tenant's saved invoice print format.
   */
  earlyPayDiscountRate?: number;
  /** Days the early-pay rate stays valid — printed in the footnote. */
  earlyPayWindowDays?: number;
  /**
   * Tenant's saved print template. Controls presentation and the optional
   * commercial terms only — never the arithmetic, which always comes from the
   * invoice header and its items. Omit (or pass `{}`) to use the built-in
   * defaults in `FALLBACK`.
   *
   * When supplied, `format.earlyPayDiscountRate` / `earlyPayWindowDays` take
   * precedence over the standalone props above, which remain for callers that
   * haven't been migrated to the template yet.
   */
  format?: PrintFormat | null;
  /**
   * Language to print in. Falls back to the format's own default language.
   * Drives both label lookup and text direction.
   */
  languageCode?: string;
  /**
   * Tenant's `isRtl` flag for the print language, when known. Overrides the
   * resolver's script detection — the tenant's own declaration beats a guess.
   */
  rtl?: boolean;
  /**
   * Product-name translations for the print language, as built by
   * `translationIndex(...)` from `@kss/api`.
   *
   * Passed in rather than fetched here because this component is deliberately
   * side-effect free — the calling page owns all data loading. Omitted means
   * line descriptions print exactly as stored, which is the correct behaviour
   * for an untranslated tenant.
   */
  productTranslations?: Map<string, string>;
}

export default function InvoiceDocument({
  invoice,
  items,
  seller,
  buyer,
  signatureSrc,
  earlyPayDiscountRate = 0,
  earlyPayWindowDays = 5,
  format,
  languageCode,
  rtl,
  productTranslations,
}: InvoiceDocumentProps) {
  /* Shared resolver — the same one reports use, so the two document families
     can never drift to different palettes, fallbacks or RTL behaviour. It
     supplies the nine colour slots, typography, page rules, direction and the
     label lookup (`theme.t`). */
  const theme = useMemo(
    () => resolvePrintTheme(format, languageCode, rtl),
    [format, languageCode, rtl],
  );

  /* Invoice-specific settings the shared theme doesn't cover: section toggles,
     the optional early-pay term and the free-text blocks. Blank strings are
     treated as "not configured" so clearing a box in the admin UI falls back
     rather than printing an empty section. */
  const tpl = useMemo(() => {
    const f = format ?? {};
    return {
      showBankDetails: f.showBankDetails !== false,
      showUpiQr: f.showUpiQr !== false,
      showSignature: f.showSignature !== false,
      showHsnColumn: f.showHsnColumn !== false,
      showAmountInWords: f.showAmountInWords !== false,
      showPlaceOfSupply: f.showPlaceOfSupply !== false,
      earlyPayRate: Number(f.earlyPayDiscountRate ?? earlyPayDiscountRate) || 0,
      earlyPayDays: Number(f.earlyPayWindowDays ?? earlyPayWindowDays) || 5,
      // Per-language variants live under the reserved label keys; the format's
      // own columns are the default-language copy.
      terms: theme.t('text.terms', f.termsAndConditions?.trim() || ''),
      declaration: theme.t('text.declaration', f.declarationText?.trim() || ''),
      footerNote: theme.t('text.footer', f.footerNote?.trim() || ''),
    };
  }, [format, earlyPayDiscountRate, earlyPayWindowDays, theme]);
  const totals = useMemo(() => {
    const sub = Number(invoice?.subTotal ?? 0);
    const discount = Number(invoice?.discountAmount ?? 0);
    const cgst = Number(invoice?.cgstAmount ?? 0);
    const sgst = Number(invoice?.sgstAmount ?? 0);
    const igst = Number(invoice?.igstAmount ?? 0);
    const roundOff = Number(invoice?.roundOff ?? 0);
    /* F2 — delivery / handling. Printed as its own summary line so the
       document actually adds up: without it the reader sees
       Sub + CGST + SGST ≠ Grand Total. This is the ₹99 that made a
       ₹1,041 order print as ₹942. */
    const shipping = Number(invoice?.shippingAmount ?? 0);
    const grand = Number(
      invoice?.totalAmount ?? sub + cgst + sgst + igst - discount + roundOff + shipping,
    );
    return { sub, discount, cgst, sgst, igst, roundOff, shipping, grand };
  }, [invoice]);

  /* Actual GST rates carried by the lines — the summary used to hardcode
     "(9%)" regardless of what the items said, which misprints every
     non-9% slab and every inter-state (IGST) invoice. */
  const gstRates = useMemo(() => {
    const pick = (sel: (i: InvoiceItem) => number | undefined) => {
      const vals = items.map((i) => Number(sel(i) ?? 0)).filter((n) => n > 0);
      return vals.length > 0 ? Math.max(...vals) : 0;
    };
    return { cgst: pick((i) => i.cgstRate), sgst: pick((i) => i.sgstRate), igst: pick((i) => i.igstRate) };
  }, [items]);

  /** Inter-state invoices carry tax on IGST — show that column instead of CGST/SGST. */
  const isIgst = totals.igst > 0 || gstRates.igst > 0;

  const earlyPayDiscount = useMemo(() => {
    const rate = tpl.earlyPayRate;
    if (rate <= 0) return null;
    const amt = Math.round(totals.grand * (rate / 100) * 100) / 100;
    return { rate, amount: amt, payable: totals.grand - amt };
  }, [totals.grand, tpl.earlyPayRate]);

  const amountInWords = useMemo(() => inrToWords(totals.grand), [totals.grand]);

  return (
    <div
      /* `invoice-doc` lets the caller's @media print rule target only
         this container, so the toolbar around it doesn't appear in
         print output. White background + black text mirrors the PDF. */
      className="invoice-doc rounded-md border border-[var(--color-border)] p-8"
      dir={theme.dir}
      /* All nine colour slots, the font and the page background come from the
         shared theme. `maxWidth` keeps the on-screen preview readable; print
         uses the format's own paper size via `theme.pageCss` below. */
      style={{ ...theme.style, maxWidth: 900, margin: '0 auto' }}
    >
      {/* Paper size / orientation / margins for the print job. */}
      <style>{theme.pageCss}</style>

      {/* ────────── 1. Title ────────── */}
      <h1 className="text-center text-2xl font-bold tracking-wide" style={{ color: 'var(--print-accent)' }}>
        {theme.t('invoice.title', theme.title || FALLBACK_TITLE)}
      </h1>

      {/* ────────── 2. Invoice no / dates / company logo ────────── */}
      <div className="mt-4 flex items-start justify-between gap-4 border-b border-[color:var(--print-accent)] pb-3">
        <div className="grid gap-1 text-sm">
          <div><strong>{theme.t('invoice.number', 'Invoice No')}:</strong> {invoice.invoiceNumber ?? '—'}</div>
          <div><strong>{theme.t('invoice.date', 'Date')}:</strong> {invoice.invoiceDate?.slice(0, 10) ?? '—'}</div>
          <div><strong>{theme.t('invoice.dueDate', 'Due Date')}:</strong> {invoice.dueDate?.slice(0, 10) ?? '—'}</div>
        </div>
        {/* Masthead. Prefers a real uploaded logo; otherwise a wordmark built
            from the template's brand fields, falling back to the seller's own
            company name rather than a hardcoded third-party brand. */}
        <div className="flex items-center gap-2 text-lg font-extrabold" style={{ color: 'var(--print-heading)' }}>
          {theme.logoUrl ? (
            <img
              src={theme.logoUrl}
              alt={theme.brandName || seller?.name || 'Logo'}
              className="max-w-[220px] object-contain"
              style={{ maxHeight: theme.logoHeightMm ? `${theme.logoHeightMm}mm` : '3.5rem' }}
            />
          ) : (
            <>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[color:var(--print-accent)] text-white">
                {(theme.brandName || seller?.name || '—').trim().charAt(0).toUpperCase()}
              </span>
              <span className="leading-tight">
                <span>{theme.brandName || seller?.name || ''}</span>
                {theme.brandTagline && (
                  <>
                    <br />
                    <span className="text-xs tracking-[0.3em] text-[color:var(--print-heading)]">
                      {theme.brandTagline}
                    </span>
                  </>
                )}
              </span>
            </>
          )}
        </div>
      </div>

      {/* ────────── 3. Billed By / Billed To ────────── */}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <PartyCard title={theme.t('invoice.billedBy', 'Billed By')} party={seller ? toParty(seller) : null} fallbackName="—" />
        <PartyCard title={theme.t('invoice.billedTo', 'Billed To')} party={buyer ? toParty(buyer) : null} fallbackName="—" />
      </div>

      {/* ────────── 4. Place / Country of supply ────────── */}
      {tpl.showPlaceOfSupply && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div><strong>{theme.t('invoice.placeOfSupply', 'Place of Supply')}:</strong> {seller?.city ?? '—'}</div>
          <div><strong>{theme.t('invoice.countryOfSupply', 'Country of Supply')}:</strong> {seller?.country?.trim() || 'India'}</div>
        </div>
      )}

      {/* ────────── 5. Items table ────────── */}
      <table className="mt-3 w-full border-collapse text-sm">
        <thead>
          <tr style={{ background: 'var(--print-thead-bg)' }} className="text-[color:var(--print-heading)]">
            <th className="border border-[color:var(--print-border)] px-2 py-1 text-left">{theme.t('col.itemDescription', 'Item Description')}</th>
            {tpl.showHsnColumn && (
              <th className="border border-[color:var(--print-border)] px-2 py-1">{theme.t('col.hsn', 'HSN/SAC Code')}</th>
            )}
            <th className="border border-[color:var(--print-border)] px-2 py-1">{theme.t('col.qty', 'Qty')}</th>
            <th className="border border-[color:var(--print-border)] px-2 py-1">{theme.t('col.gstPct', 'GST %')}</th>
            <th className="border border-[color:var(--print-border)] px-2 py-1 text-right">{theme.t('col.taxableAmount', 'Taxable Amount')}</th>
            {isIgst ? (
              <th className="border border-[color:var(--print-border)] px-2 py-1 text-right">
                IGST Amount{gstRates.igst ? ` (${gstRates.igst}%)` : ''}
              </th>
            ) : (
              <>
                <th className="border border-[color:var(--print-border)] px-2 py-1 text-right">
                  SGST Amount{gstRates.sgst ? ` (${gstRates.sgst}%)` : ''}
                </th>
                <th className="border border-[color:var(--print-border)] px-2 py-1 text-right">
                  CGST Amount{gstRates.cgst ? ` (${gstRates.cgst}%)` : ''}
                </th>
              </>
            )}
            <th className="border border-[color:var(--print-border)] px-2 py-1 text-right">{theme.t('col.total', 'Total')}</th>
          </tr>
        </thead>
        <tbody>
          {(items.length > 0 ? items : [emptyRow()]).map((it, i) => {
            const taxable = Number(it.taxableAmount ?? it.quantity * it.unitPrice);
            const sgst = Number(it.sgstAmount ?? 0);
            const cgst = Number(it.cgstAmount ?? 0);
            const igst = Number(it.igstAmount ?? 0);
            const gstPct = Number((it.cgstRate ?? 0) + (it.sgstRate ?? 0) + (it.igstRate ?? 0));
            const line = Number(it.total ?? taxable + sgst + cgst + igst);
            return (
              <tr key={it.invoiceItemId ?? `r-${i}`}>
                <td className="border border-[color:var(--print-border)] px-2 py-1">
                  {/* `NameCell` from the panel's cross-cutting hook is
                     admin-only; the customer app has no equivalent
                     server-side name resolver, and shipping a data
                     fetch inside a pure presentational component
                     would break the extraction. Fall back to a dash
                     when neither an itemName nor a productId is set. */}
                  {/* Translated product name where one exists, else the name
                      snapshotted on the invoice line. A missing translation must
                      never blank the description — the fallback chain in
                      `resolveTranslated` guarantees the stored value wins over
                      nothing. */}
                  {resolveTranslated(
                    productTranslations,
                    it.productId,
                    'Name',
                    it.itemName ?? (it.productId ? `Product ${String(it.productId).slice(0, 8)}` : '—'),
                  )}
                </td>
                {tpl.showHsnColumn && (
                  <td className="border border-[color:var(--print-border)] px-2 py-1 text-center">{it.hsnCode ?? '—'}</td>
                )}
                <td className="border border-[color:var(--print-border)] px-2 py-1 text-center">{Number(it.quantity ?? 0)}</td>
                <td className="border border-[color:var(--print-border)] px-2 py-1 text-center">{gstPct || '—'}</td>
                <td className="border border-[color:var(--print-border)] px-2 py-1 text-right">₹{taxable.toFixed(2)}</td>
                {isIgst ? (
                  <td className="border border-[color:var(--print-border)] px-2 py-1 text-right">₹{igst.toFixed(2)}</td>
                ) : (
                  <>
                    <td className="border border-[color:var(--print-border)] px-2 py-1 text-right">₹{sgst.toFixed(2)}</td>
                    <td className="border border-[color:var(--print-border)] px-2 py-1 text-right">₹{cgst.toFixed(2)}</td>
                  </>
                )}
                <td className="border border-[color:var(--print-border)] px-2 py-1 text-right">₹{line.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ────────── 6. Bank Details / Invoice Summary ────────── */}
      <div className={`mt-5 grid gap-4 ${tpl.showBankDetails ? 'md:grid-cols-2' : ''}`}>
        {tpl.showBankDetails && (
        <div className="rounded-md border border-[color:var(--print-border)] p-3 text-sm">
          <h3 className="mb-2 font-semibold text-[color:var(--print-accent)]">{theme.t('invoice.bankDetails', 'Bank Details')}</h3>
          {(() => {
            /* Never invent remittance details. These previously fell back to a
               hardcoded Kotak account number + IFSC, so any tenant whose
               Company record had no bank details printed a real-looking
               account that was not theirs — an invoice telling customers to
               pay into the wrong account. Show only what the tenant actually
               configured; the block hides itself when nothing is set. */
            const bankName = seller?.bankName?.trim() || '';
            const acct = seller?.bankAccountNumber?.trim() || '';
            const ifsc = seller?.bankIfsc?.trim() || '';
            const branch = seller?.bankBranch?.trim() || '';
            const hasBank = Boolean(bankName || acct || ifsc);
            const upiId = seller?.upiId?.trim() || '';
            const phonepe = seller?.phonepeNumber?.trim() || '';
            const gpay = seller?.googlePayUpi?.trim() || upiId;
            const upiQr = seller?.upiQrUrl?.trim() || '';
            const upiPayload =
              upiId &&
              `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(seller?.name ?? '')}&cu=INR&am=${totals.grand.toFixed(2)}`;
            const fallbackQr =
              upiPayload &&
              `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(upiPayload)}`;
            const qrSrc = upiQr || fallbackQr || '';
            return (
              <div className="flex items-start gap-3">
                <div className="flex-1 grid grid-cols-[110px_1fr] gap-y-1">
                  {!hasBank && !upiId && (
                    <span className="col-span-2 text-content-muted">
                      No payment details configured.
                    </span>
                  )}
                  {bankName && (<><span className="font-semibold">Bank Name:</span><span>{bankName}</span></>)}
                  {acct && (<><span className="font-semibold">Account No:</span><span>{acct}</span></>)}
                  {ifsc && (<><span className="font-semibold">IFSC Code:</span><span>{ifsc}</span></>)}
                  {branch && (<><span className="font-semibold">Branch:</span><span>{branch}</span></>)}
                  {upiId && (<><span className="font-semibold">UPI ID:</span><span>{upiId}</span></>)}
                  {phonepe && (<><span className="font-semibold">PhonePe No:</span><span>{phonepe}</span></>)}
                  {gpay && gpay !== upiId && (<><span className="font-semibold">Google Pay UPI:</span><span>{gpay}</span></>)}
                </div>
                {qrSrc && tpl.showUpiQr && (
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={qrSrc}
                      alt="Scan & Pay UPI QR"
                      className="h-[120px] w-[120px] rounded border border-[color:var(--print-border)] bg-white object-contain p-1"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                    />
                    <span className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--print-heading)]">
                      Scan &amp; Pay
                    </span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        )}
        <div className="rounded-md border border-[color:var(--print-border)] p-3 text-sm">
          <h3 className="mb-2 font-semibold text-[color:var(--print-accent)]">{theme.t('invoice.summary', 'Invoice Summary')}</h3>
          <SumRow label={theme.t('totals.subTotal', 'Sub Total')} value={totals.sub} />
          <SumRow label={theme.t('totals.discount', 'Discount:')} value={totals.discount} muted={totals.discount === 0} />
          <SumRow label={theme.t('totals.taxableAmount', 'Taxable Amount')} value={totals.sub - totals.discount} />
          {isIgst ? (
            <SumRow label={`IGST${gstRates.igst ? ` (${gstRates.igst}%)` : ''}:`} value={totals.igst} />
          ) : (
            <>
              <SumRow label={`CGST${gstRates.cgst ? ` (${gstRates.cgst}%)` : ''}:`} value={totals.cgst} />
              <SumRow label={`SGST${gstRates.sgst ? ` (${gstRates.sgst}%)` : ''}:`} value={totals.sgst} />
            </>
          )}
          {totals.shipping > 0 && <SumRow label={theme.t('totals.shipping', 'Shipping / Handling:')} value={totals.shipping} />}
          {totals.roundOff !== 0 && <SumRow label={theme.t('totals.roundOff', 'Round Off:')} value={totals.roundOff} />}
          <div className="my-2 border-t border-[color:var(--print-border)]" />
          <div className="flex items-baseline justify-between text-base font-bold">
            <span>{theme.t('totals.grandTotal', 'Grand Total')}</span>
            <span>₹{totals.grand.toFixed(2)}</span>
          </div>
          {tpl.showAmountInWords && (
            <>
              <p className="mt-1 text-xs italic text-content-secondary">{theme.t('invoice.amountInWords', 'Amount In Words')}:</p>
              <p className="text-sm font-semibold italic">{amountInWords}</p>
            </>
          )}
          {earlyPayDiscount && (
            <>
              <div className="my-2 border-t border-[color:var(--print-border)]" />
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-semibold text-[#c93d3d]">
                  Early Pay Discount ({earlyPayDiscount.rate}%):
                </span>
                <span className="font-semibold text-[#c93d3d]">
                  - ₹{earlyPayDiscount.amount.toFixed(2)}
                </span>
              </div>
              <div className="flex items-baseline justify-between text-sm font-bold">
                <span>Early Pay Amount</span>
                <span>₹{earlyPayDiscount.payable.toFixed(2)}</span>
              </div>
              <p className="mt-0.5 text-[11px] text-content-muted">
                *If paid within {tpl.earlyPayDays} days
              </p>
            </>
          )}
        </div>
      </div>

      {/* ────────── 7. Terms / Notes / Signature ────────── */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          {/* Terms come from the tenant's saved template, then the invoice's own
              notes. The previous fallback asserted a 15-day due date and 18% p.a.
              interest on every invoice — enforceable-sounding terms nobody had
              agreed to. Nothing is printed when nothing is configured. */}
          {(tpl.terms || invoice.notes) && (
            <div className="rounded-md border border-[color:var(--print-border)] p-3">
              <h3 className="mb-1 font-semibold text-[color:var(--print-accent)]">{theme.t('invoice.terms', 'Terms & Conditions')}</h3>
              <p className="whitespace-pre-line text-sm">
                {tpl.terms || invoice.notes}
              </p>
            </div>
          )}
          {tpl.declaration && (
            <div className="mt-3 rounded-md border border-[color:var(--print-border)] p-3">
              <h3 className="mb-1 font-semibold text-[color:var(--print-accent)]">{theme.t('invoice.declaration', 'Declaration')}</h3>
              <p className="whitespace-pre-line text-sm">{tpl.declaration}</p>
            </div>
          )}
        </div>
        {tpl.showSignature && (
        <div className="flex flex-col items-end justify-end text-sm">
          <p className="font-semibold">For {seller?.name ?? '—'}</p>
          {/* SSO ISSUE 4 — render the uploaded signature PNG when the
              invoice carries one; otherwise fall back to the dashed
              placeholder so the layout stays stable. Each caller
              resolves the URL through its own `toAbsoluteImageUrl`
              (panel and web have separate resolvers). */}
          {signatureSrc ? (
            <img
              src={signatureSrc}
              alt="Authorized signature"
              className="my-2 h-20 w-32 object-contain"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
            />
          ) : (
            <div className="my-2 h-20 w-32 rounded-full border border-dashed border-[color:var(--print-accent)]" />
          )}
          <p className="font-semibold">{theme.t('invoice.authorisedSignature', 'Authorized Signature')}</p>
        </div>
        )}
      </div>

      {/* ────────── 8. Footer ────────── */}
      {/* Contact line uses the tenant's own footer note when set. The previous
          fallbacks printed a support address and phone number belonging to the
          software vendor, not to the tenant issuing the invoice — so customers
          were directed to the wrong party. Falls back to nothing. */}
      {(tpl.footerNote || seller?.email || seller?.phone) && (
        <div className="mt-6 border-t border-[color:var(--print-border)] pt-3 text-xs">
          {tpl.footerNote ? (
            <span className="whitespace-pre-line">{tpl.footerNote}</span>
          ) : (
            <>
              For any inquiries
              {seller?.email && (<>, email us at <strong>{seller.email}</strong></>)}
              {seller?.phone && (<> or contact us at <strong>{seller.phone}</strong></>)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Helpers                                                            */
/* ────────────────────────────────────────────────────────────────── */

interface PartyView {
  name?: string;
  addressLine?: string;
  gstin?: string;
  phone?: string;
  email?: string;
}
function toParty(c: Company | Customer): PartyView {
  const anyC = c as unknown as Record<string, unknown>;
  const addr = [
    anyC.addressLine1 ?? anyC.address ?? '',
    anyC.addressLine2 ?? '',
    anyC.city ?? '',
    anyC.state ?? '',
    anyC.pincode ?? '',
  ]
    .map((s) => String(s ?? '').trim())
    .filter(Boolean)
    .join(', ');
  return {
    name: String(anyC.name ?? ''),
    addressLine: addr,
    gstin: String(anyC.gstin ?? anyC.gstNumber ?? 'NA'),
    phone: String(anyC.phone ?? anyC.contactPhone ?? ''),
    email: String(anyC.email ?? anyC.contactEmail ?? ''),
  };
}

function PartyCard({
  title,
  party,
  fallbackName,
}: {
  title: string;
  party: PartyView | null;
  fallbackName: string;
}) {
  return (
    <div className="rounded-md border border-[color:var(--print-border)] p-3 text-sm">
      <h3 className="mb-1 font-semibold text-[color:var(--print-accent)]">{title}</h3>
      <p className="font-bold">{party?.name?.trim() || fallbackName}</p>
      {party?.addressLine && <p className="text-content-secondary">{party.addressLine}</p>}
      <p>GSTIN: {party?.gstin ?? 'NA'}</p>
      {party?.phone && <p>Phone: {party.phone}</p>}
      {party?.email && <p>Email: {party.email}</p>}
    </div>
  );
}

function SumRow({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-0.5 text-sm">
      <span className={muted ? 'text-content-muted' : ''}>{label}</span>
      <span className={muted ? 'text-content-muted' : 'font-semibold'}>
        ₹{Number(value ?? 0).toFixed(2)}
      </span>
    </div>
  );
}

function emptyRow(): InvoiceItem {
  return {
    invoiceId: '',
    productId: '',
    quantity: 0,
    unitPrice: 0,
    total: 0,
    itemName: '(No items)',
  };
}

/**
 * INR amount → words. Implementation matches the Indian numbering
 * system shown in the reference PDF ("Four Thousand One Hundred
 * Thirty Rupees Only").
 */
function inrToWords(n: number): string {
  if (!isFinite(n) || n === 0) return 'Zero Rupees Only';
  const rupees = Math.floor(n);
  const paise = Math.round((n - rupees) * 100);
  const rupeeWords = numToWords(rupees) + ' Rupees';
  const paiseWords = paise > 0 ? ' and ' + numToWords(paise) + ' Paise' : '';
  return rupeeWords + paiseWords + ' Only';
}
const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function twoDigit(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  const t = Math.floor(n / 10);
  const o = n % 10;
  return TENS[t] + (o ? ' ' + ONES[o] : '');
}
function threeDigit(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  return (h ? ONES[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? twoDigit(r) : '');
}
function numToWords(n: number): string {
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  const crore = Math.floor(n / 10000000);
  if (crore) { parts.push(twoDigit(crore) + ' Crore'); n %= 10000000; }
  const lakh = Math.floor(n / 100000);
  if (lakh) { parts.push(twoDigit(lakh) + ' Lakh'); n %= 100000; }
  const thousand = Math.floor(n / 1000);
  if (thousand) { parts.push(twoDigit(thousand) + ' Thousand'); n %= 1000; }
  if (n) parts.push(threeDigit(n));
  return parts.join(' ').trim();
}
